import { fn, INamed } from '../utils';
import ToDictionary = fn.ToDictionary;
import { Bind as kBind, GetPropDisplayValue, IPropDisplayValueConfig, AsObservableArray, ReplaceObservableData } from '../navkendo/kutils';
import { IPageProperty, IPageObj, TDefaultPageObj, IPageMetaData, TPagePropertyAccess, ILookupInfo, TPagePropRefs } from '../nav/navtypes';
import { getModel, getClientKeyFunc, valHasChanged, setModel, IObservableArray, enforceTyped, Model, ObservableArray, Observable } from './navmodel';
import { INavPageClientInfo, $PageItemExecutedUpdate } from './navmodel';
import { NavPageItem } from './navpageitem';
import { INavMethodConfig } from '../navdata/navdata';
import { IsNavPageItem } from './indirection';

/** Common Base Class for "root" Property Item and "Line" Property Item */
export class NavPropertyItem<TPage extends IPageObj = TDefaultPageObj> {

    /** Access To Root Item (Parent for Line Items, self otherwise) */
    public readonly Root: NavPageItem;

    /** Identifier for this Instance (Unique but Valid between callbacks / Updates) */
    public readonly ClientKey: string;

    /** Current "Server" Key, Auto Synced internally on Server Operations (Update, ExecuteMethod), just as Model ... (Object.define Property) DO NOT SET */
    public get Key(): string {
        return (this.Model && this.Model.Key) || "";
    }

    /** Get Unique ClientKey  for a model Object, that has the 'structure' of a raw Item given by this schema */
    public GetKey: (model: any, noError?: boolean) => string;

    /** Resolve Property By Name /Property Instance to 'Owned' Property Instance, opt. throwing Error if not available */
    public ResolveProperty(prop: TPagePropertyAccess<TPage>, throwError?: boolean, useAlias?: boolean): IPageProperty {
        return this.MetaData.ResolveProperty(prop, throwError, useAlias);
    }

    constructor(_parentOrNull: NavPageItem, public readonly MetaData: IPageMetaData<TPage>, public readonly Model: Model & Readonly<TPage>) {
        this.GetKey = getClientKeyFunc(MetaData);
        this.ClientKey = this.GetKey(Model);
        let root = this.Root = _parentOrNull ? _parentOrNull : (IsNavPageItem(this) ? this : undefined);
        if (!(root instanceof NavPropertyItem))
            throw new Error("Invalid Root Object");
    }

    /** Check if a 'Key'/Model/Instance is Representing the same element */
    public IsMatch(idOrModel: any) {
        if (typeof (idOrModel) == "string")
            return (idOrModel != "") && (this.ClientKey == idOrModel || this.Model.Key == idOrModel);
        if (idOrModel instanceof NavPropertyItem)
            return this.IsMatch((<NavPropertyItem>idOrModel).ClientKey);
        if (idOrModel instanceof Observable)
            return this.IsMatch(this.GetKey(idOrModel, true));
        return this == idOrModel || (idOrModel && (this.Model == idOrModel));
    }

    /** register Property Changed Notification for property on this instance (prop = null or '*' means :
     * all changes get notified, returns deregistration function */
    RegisterPropertyChanged(propRef: TPagePropRefs<TPage>, callback: (ev: { Prop: IPageProperty; Item: NavPropertyItem }) => void) {
        var root = this.Root,
            anchor = root.Anchor;
        if (<NavPropertyItem>root !== this) {
            return anchor.RegisterPropertyChanged(<any>propRef, (ev) => {
                if (ev.Item === this)
                    callback(ev);
            });
        } else {
            return anchor.RegisterPropertyChanged(<any>propRef, callback);
        }
    }

    /** Register on Method Executed */
    RegisterMethodExecuted(callback: (method: string) => void, ...methodNames: string[]) {
        return kBind(this.Model, (m?: { MethodConfig?: INavMethodConfig }) => {
            var currMethodName = m && m.MethodConfig && m.MethodConfig.MethodName;
            if (currMethodName && methodNames.indexOf(currMethodName) >= 0) {
                callback(currMethodName);
            }
        }, $PageItemExecutedUpdate);
    }

    /** Get Typed Value for Property By Name:
     * String, DateTime, Boolean, Decimal, Int32 : Typed Value
     * Enum : as Number
     * Array : see ObservableArray
     */
    public GetValue<TProp extends keyof TPage>(prop: TProp | IPageProperty, throwError?: boolean, useAlias?: boolean): TPage[TProp] {
        prop = this.ResolveProperty(prop, !!throwError, useAlias);
        if (!prop)
            return;
        return getModel(this.Model, <any>prop.Name);
    }

    /**  Get Value with Custom EnumHandling : Get Typed enum Name as String */
    public GetValueEx<TProp extends keyof TPage>(prop: TProp | IPageProperty, throwError?: boolean, useAlias?: boolean) {
        prop = this.ResolveProperty(prop, !!throwError, useAlias);
        if (!prop)
            return;
        var result = getModel(this.Model, <any>prop.Name);
        if (prop.EnumMappings) {
            var check = prop.EnumMappings.filter(item => item.Value == <any>result)[0];
            if (check && check.Enum)
                return check.Enum;
        }

        return result;
    }

    /** Get Value as String For Display */
    public GetDisplayValue<TProp extends keyof TPage>(prop: TProp | IPageProperty, config?: IPropDisplayValueConfig): string {
        prop = this.ResolveProperty(prop, false);
        if (!prop)
            return;

        var value = <any>getModel(this.Model, <any>prop.Name);

        return GetPropDisplayValue(value, prop, config);
    }

    /** Check if Value is Invalid! */
    GetIsInvalid(propx: keyof TPage | IPageProperty) {
        if (!this.Root.Anchor.Mandatory.GetValue(<string>propx, this))
            return false; // prop is not mandatory

        // NO : Event Mandatory + ReadOnly Properties are invalid!
        //if (prop.ReadOnly || this.Root.Readonly.GetValue(prop, this))
        //    return false;
        return this.GetIsEmpty(propx);
    }

    /** Check if Valuf of given Property Is Empty */
    GetIsEmpty(propx: keyof TPage | IPageProperty) {
        var prop = this.ResolveProperty(propx, true);
        switch (prop.Type) {
            case "Boolean":
                return false;
            case "DateTime":
                var dtValue = getModel(this.Model, <any>prop.Name);
                return !(fn.IsDate(dtValue) && (dtValue.valueOf()  > 0));
            case "String":
            case "Decimal":
            case "Int32":
                var rawValue = <any>getModel(this.Model, <any>prop.Name);
                return rawValue == null || (rawValue == undefined) || rawValue == "";
            default:
                if (prop.EnumMappings) {
                    var emptyEnum = prop.EnumMappings.Empty;
                    return emptyEnum && (getModel(this.Model, <any>prop.Name) == <any>emptyEnum.Value);
                }
                else
                    return false;
        }
    }

    /** Create Readonly Access to Model:
    *  result is a plain Object with readonly access to Propertes,
    * write Access will throw exception! */
    public AsReadonly() {
        if (this._AsReadonly)
            return this._AsReadonly;

        // Get Or Create Access Factory for MetaData
        var raMarker = "__ReadOnlyAccess",
            metaData = this.MetaData,
            access = <(pageItem: NavPropertyItem) => void>metaData[raMarker];
        if (!access) {
            Object.defineProperty(metaData, raMarker, {
                enumerable: false,
                writable: false,
                value: access = CreateReadOnlyAccess(metaData)
            });
        }
        return this._AsReadonly = new access(this);

        // Create Readonly Access factory for metaData
        function CreateReadOnlyAccess(metaData: IPageMetaData) {
            const itemAccess = "__propItem" + fn.id();
            function ReadOnlyAccess(propItem: NavPropertyItem) {
                Object.defineProperty(this, itemAccess, {
                    writable: false,
                    enumerable: false,
                    value: propItem
                });
            }

            // Create JS Properties for metadata properties
            metaData.Properties.forEach(p => {
                var propName = p.Name;
                if (p.ArrayProperties)
                    var getter = function () {
                        var me = this,
                            propItem = <NavPageItem>me[itemAccess];
                        return propItem.GetLineData(propName).Items.map(item => item.AsReadonly());
                    };
                else
                    getter = function () {
                        var me = this,
                            pageItem = <NavPropertyItem>me[itemAccess];
                        return pageItem.GetValueEx(propName);
                    };
                Object.defineProperty(ReadOnlyAccess.prototype, propName, {
                    enumerable: true,
                    get: getter,
                    set: function () {
                        throw new Error("Property '" + propName + "' is readonly!");
                    }
                });
            });

            return ReadOnlyAccess;
        }
    }
    private _AsReadonly: any;

    /** Resolve Current Lookup Info (Imp Resetted Promises on Value Change (any change right now, optimization?) */
    public ResolveLookupInfo(property: any): PromiseLike<{ LookupInfo: ILookupInfo; TargetPageInfo: INavPageClientInfo }> {
        var prop = this.ResolveProperty(property, false);
        if (!prop && !prop.HasLookup)
            return this.Root.Service.$c.reject("No Property given or not a valid Lookup Property");

        if (!this.resolvedLookups) {
            this.resolvedLookups = {};
            var me = this,
                // TODO : check need to deregister
                deregister = this.Root.Anchor.RegisterPropertyChanged(null, () => {
                    Object.getOwnPropertyNames(me.resolvedLookups).forEach(name => {
                        var value = me.resolvedLookups[name];
                        if (!value.IsStatic)
                            delete me.resolvedLookups[name];
                    });
                });
        }
        var result = this.resolvedLookups[prop.Name];
        if (!result) {
            var resolvedLookup: ILookupInfo;
            result = this.resolvedLookups[prop.Name] = {
                Promise: this.Root.Service.GetLookupInfo(this, prop).then(l => {
                    resolvedLookup = l;
                    return this.Root.Service.GetPageClientInfo(l.TargetPageName, this.Root.ClientInfo.Url);
                }).then(pgInfo => {
                    result.IsStatic = resolvedLookup && resolvedLookup.IsStatic;
                    return {
                        LookupInfo: resolvedLookup,
                        TargetPageInfo: pgInfo,
                    };
                }), IsStatic: false
            };
        }

        return result.Promise;
    }
    private resolvedLookups: INamed<{
        Promise: PromiseLike<{
            LookupInfo: ILookupInfo;
            TargetPageInfo: INavPageClientInfo
        }>;
        IsStatic: boolean
    }> = null;

    /** Set Value in Appropriate Type */
    public SetValue(name: string | IPageProperty, uncheckedValue: any, explicitAutoUpdate?: boolean) {
        return this.Root.SetBoundValue(this, name, uncheckedValue, explicitAutoUpdate);
    }

    /** Set Mutliple Values, one after the other, IGNORING AutoUpdate */
    public SetValues(propNames: string[], getValue: (name) => any, explicitAutoUpdate?: boolean) {
        var $c = this.Root.Service.$c;
        try {
            var promises = propNames.map(name => () => this.SetValue(name, getValue(name), explicitAutoUpdate), this);
            var ops = promises.map(promise => this.Root.Anchor.EnqueueOperation(promise));
            return $c.all(ops).then(() => this);
        } catch (err) {
            return $c.reject(err);
        }
    }

    /** Übertrage Daten (z.B vom Server) auf diese Instanz */
    assign(sourceItemOrModel: any) {
        var source = sourceItemOrModel.Model || sourceItemOrModel,
            me = this,
            hasChanges = false,
            dest = this.Model;
        this.MetaData.Properties.forEach(prop => {
            if (prop.ArrayProperties) {
                var destLine = <IObservableArray<Model>>dest[prop.Name];
                var sourceLine = <ArrayLike<Model>>source[prop.Name];
                var getKeyFunc = getClientKeyFunc(prop.ArrayProperties);
                var lineData = me.Root.GetLineData(prop);
                var joinedData = fn.GroupBy(fn.OuterJoin(<ArrayLike<Model>>destLine, sourceLine, getKeyFunc, (d, s, key) => {
                    var destItem = d ? lineData.GetItems(key)[0] : undefined;
                    var mode = d ? (destItem ? (s ? "Assign" : "Delete") : "??") : "Add";
                    return {
                        DestItem: destItem,
                        Dest: d,
                        Source: s,
                        Mode: mode,
                        AddedItem: mode == "Add" ? new NavPropertyItem<any>(me.Root, prop.ArrayProperties, s) : undefined
                    };
                }), i => i.Mode);

                // Handle Delete Data
                var deleteData = joinedData["Delete"];
                if (deleteData && deleteData.length > 0) {
                    hasChanges = true;
                    deleteData.forEach(item => {
                        destLine.remove(item.Dest);
                        lineData.NotifyDeleted(item.DestItem.ClientKey);
                    });
                }
                // Handle Add Data
                var addData = joinedData["Add"];
                if (addData && addData.length > 0) {
                    hasChanges = true;
                    lineData.NotifyAdded(addData.map(a => a.AddedItem));
                    destLine.push(...addData.map(item => item.Source));
                }
                // Handled Changed Data
                var assignData = joinedData["Assign"];
                if (assignData)
                    assignData.forEach(item => hasChanges = (item.DestItem.assign(item.Source) || hasChanges));
                if (addData || deleteData || hasChanges) {
                    // TODO : What about partial changes?
                    this.Root.NotifyValueChanged(this, prop);
                }
            } else {
                var newValue, oldValue;
                if (valHasChanged(prop, newValue = source[prop.Name], oldValue = dest[prop.Name])) {
                    hasChanges = true;
                    setModel(dest, prop.Name, newValue);
                    this.Root.NotifyValueChanged(this, prop);
                }
            }
        }, this);

        return hasChanges;
    }

    /** Get a "Simple" Object Containing the Changed Items from "me", compared to "other" (to be used to sent to server!) */
    public static GetChanges(me: any, other: any, MetaData: IPageMetaData, config: { withKeys: boolean; hasChanges?: boolean }, ignoreUnchanged?: boolean) {
        if (!other)
            return;

        me = me.Model || me;
        other = other.Model || other;

        config = config || { hasChanges: false, withKeys: false };
        var result = <any>{};
        var hasChange = false;

        MetaData.Properties.forEach(item => {
            var myValue = me[item.Name];
            var otherValue = other[item.Name];
            if (myValue == undefined || otherValue == undefined) {
                if (otherValue != myValue) {
                    result[item.Name] = myValue;
                    hasChange = true;
                }
            } else {
                if (item.ArrayProperties) {
                    var changes = GetLineChanges(myValue, item.ArrayProperties, otherValue, config);
                    if (changes) {
                        result[item.Name] = changes;
                        hasChange = true;
                    }
                } else {
                    if (myValue != otherValue) {
                        var typed1 = enforceTyped(item, myValue);
                        var typed2 = enforceTyped(item, otherValue);
                        if (valHasChanged(item, typed1, typed2)) {
                            result[item.Name] = myValue;
                            hasChange = true;
                        }
                    }
                }
            }
        });

        if (MetaData.KeyFields && config.withKeys) {
            if (me["Key"])
                result["Key"] = me["Key"];
            MetaData.KeyFields.forEach(item => {
                result[item] = me[item];
            });
        }

        if (!hasChange && ignoreUnchanged == true)
            return undefined;

        if (hasChange)
            config.hasChanges = true;

        // if withKeys and it's a Root Element return element anyways
        return hasChange || config.withKeys ? result : undefined;
    }

    /** Register to be notified on availability  */
    public RegisterEnumStateChanged(prop: IPageProperty, cb: ((e: { Invalid: number[]}) => void)) {
        var curr = this.GetCustomInvalidEnumData(prop, false)?.Current;
        if (curr) {
            cb({ Invalid : fn.ToArray(curr) }); // Reflect state on startup if necessary
        }
        return kBind(this.Model, cb, "EnumInvalidChanged_" + prop.Name);
    }
    /** Dynamic Custom Enum , Caution: so far only one registration per */
    public GetCustomInvalidEnumData(prop: TPagePropertyAccess<TPage>, impCreate: boolean = true) {
        const me = this;
        const p = this.ResolveProperty(prop);
        class CustomInvalidInfo {
            public readonly Current = AsObservableArray<number>([]);
            public SetInvalid(mappings?: number[]) {
                const changed = ReplaceObservableData(this.Current, (mappings || []), item => item, item => true);
                if (changed) {
                  this.Notify();
                }
            }

            public Notify() {
                me.Model.trigger("EnumInvalidChanged_" + p.Name, { Invalid: fn.ToArray(this.Current) });
            }
        }
        if (p && p.EnumMappings) {
            const thisProp = "_customEnumData";
            var customEnumData: Record<string, CustomInvalidInfo> = (this[thisProp] || (this[thisProp] = {}));
            return customEnumData[p.Name] || (impCreate && (customEnumData[p.Name] = new CustomInvalidInfo()));
        }
    }
}

 /** Get Line Changes in a manner that can be sent to server */
 function GetLineChanges(items: ObservableArray, MetaData: IPageMetaData, checkOther: ObservableArray, config: { withKeys: boolean }) {

    if (!checkOther)
        return;
    var getLineKey = getClientKeyFunc(MetaData);
    var namedOthers = ToDictionary(checkOther, getLineKey);

    // Add/Deletes can't be handled in NAV in this way anyways ...
    var changed = items.map(item => NavPropertyItem.GetChanges(item, namedOthers[getLineKey(item)], MetaData, config, true)).filter(item => !!item);

    if (changed.length == 0)
        return;

    return changed;
}
